import { useEffect, useState } from "react";
import { onRoadSegmentsChanged, prefetchSegmentsForBbox } from "@/api/roadSegments";
import { bbox as bboxOf, type LngLat } from "@/utils/polyline";
import { prefetchNominationsForRoutes } from "@/api/nominations";
import { prefetchRoadNominationsForRoutes } from "@/api/roadNominations";

interface RouteLike {
  route: { coordinates: LngLat[] };
}

/**
 * Warms the synchronous community-data caches whenever the current scored
 * route set changes, and exposes a `segmentRev` counter that bumps every
 * time the underlying road-segment cache emits a "changed" event (e.g. the
 * user just rated a street mid-trip). Consumers re-score off `segmentRev`.
 */
export function useSegmentPrefetch(scored: RouteLike[]): number {
  // Warm the user-nominated-neighborhoods cache + the proprietary
  // road-segment aggregate layer so the synchronous community scorer can
  // read them on the next render.
  useEffect(() => {
    if (scored.length === 0) return;
    const all = scored.flatMap((s) => s.route.coordinates);
    void prefetchNominationsForRoutes(all);
    void prefetchRoadNominationsForRoutes(all);
    const b = bboxOf(all, 0.005);
    void prefetchSegmentsForBbox({
      minLat: b.minLat, maxLat: b.maxLat, minLng: b.minLng, maxLng: b.maxLng,
    });
  }, [scored]);

  const [segmentRev, setSegmentRev] = useState(0);
  useEffect(() => onRoadSegmentsChanged(() => setSegmentRev((n) => n + 1)), []);
  return segmentRev;
}
